import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

// ── ONE browser client per LOGGED-IN STAFF, for the life of the tab (RT-2) ──
//
// This module used to recreate the client whenever sb_jwt changed, and since
// RT-1 (#774) tore the old client's socket down with removeAllChannels().
// RT-1 also made rotation ROUTINE: /api/heartbeat re-mints sb_jwt once it has
// under 6h of its 12h life left (sbJwtNeedsRemint). Before that, rotation only
// happened at login/logout — which hard-navigates, so nothing was left to
// orphan. Twenty-one channels are subscribed across nineteen files; only two
// keyed their effect on the token, so from #774 onward every rotation silently
// unsubscribed the other nineteen for the rest of the session — the inbox
// conversation LIST stopped moving while the open thread (ThreadPane, which
// did rebuild) kept working. "Mensajes llegando a destiempo."
//
// Why those channels stay dead: their effects simply never re-run, so nothing
// rebuilds them. (The component cleanup is not a harmless no-op either —
// RealtimeClient.removeChannel() unsubscribes the channel it is handed
// regardless of which client owns it, then disconnects if the CURRENT client's
// array is empty, so a late unmount can take down the freshly built socket.)
//
// The fix is to stop invalidating the client. A rotated token goes to the LIVE
// socket via realtime.setAuth(), which updates every channel's join payload and
// pushes `access_token` to the joined ones (RealtimeClient._performAuth) — no
// teardown, no rejoin, no gap to backfill. REST reads the current cookie per
// request through the `accessToken` option.
//
// Do NOT "fix" this by adding a token to the 19 dep arrays instead:
// RealtimeClient.channel(topic) returns the EXISTING channel for a topic and
// subscribe() no-ops unless state === 'closed', so a same-tick teardown +
// resubscribe on a preserved client returns the dying channel and never
// rejoins. That is the CE-119 failure already documented in staff/page.tsx.
// Keeping the fix inside the client is also the point: nothing outside this
// file has to know that sb_jwt rotates, so a future channel author cannot
// forget to opt in.
//
// NOTE: with `accessToken` set, supabaseBrowser.auth.<x> throws by design.
// This app has no Supabase Auth — sessions are custom HMAC cookies — and
// nothing in src/ touches `.auth`. Keep it that way.

let _client: SupabaseClient<Database> | null = null;
/** `sub` claim of the sb_jwt the CURRENT client was built for. Identity, not
 *  token: rotation changes the token every ~6h but never the subject. */
let _builtForSub: string | null = null;
/** The sb_jwt last handed to the realtime socket. */
let _realtimeJwt: string | null = null;
let _watching = false;

function getJwtFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)sb_jwt=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/** `sub` (staff id) of a JWT, decoded WITHOUT verification — good for deciding
 *  "same human?", never for trust. Mirrors jwtExpMs in session-keepalive-core. */
export function sbJwtSubject(token: string | null | undefined): string | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64));
    return typeof payload.sub === 'string' ? payload.sub : null;
  } catch {
    return null;
  }
}

/** Push a rotated sb_jwt onto the live realtime socket. Idempotent: does
 *  nothing until the cookie actually changes, so it is safe on a timer, on
 *  every proxy access, and after every heartbeat. */
export function syncSupabaseAuth(): void {
  const client = _client;
  if (!client) return;
  const jwt = getJwtFromCookie();
  if (jwt === _realtimeJwt) return;
  const prev = _realtimeJwt;
  _realtimeJwt = jwt;
  // No cookie (logged out / mid-logout): leave the socket alone. The server
  // enforces exp, and logout navigates away.
  if (!jwt) return;
  void client.realtime.setAuth(jwt).catch(() => {
    // Roll the marker back so the 30s watch RETRIES instead of pinning a
    // token the socket never actually received.
    if (_client === client && _realtimeJwt === jwt) _realtimeJwt = prev;
  });
}

/** Watch the cookie on idle tabs (nobody touching the proxy). One watcher per
 *  tab, same lifetime as the module. Replaces the two useSbJwt() intervals. */
function startCookieWatch(): void {
  if (_watching || typeof document === 'undefined') return;
  _watching = true;
  setInterval(syncSupabaseAuth, 30_000);
  document.addEventListener('visibilitychange', syncSupabaseAuth);
  if (typeof window !== 'undefined') window.addEventListener('focus', syncSupabaseAuth);
}

function getClient(): SupabaseClient<Database> {
  if (_client) {
    // Cheapest rotation detector: the proxy already re-read the cookie on
    // every property access before RT-2.
    syncSupabaseAuth();
    return _client;
  }

  const url = process.env.NEXT_PUBLIC_SUPABASE_URL!;
  const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!;
  const jwt = getJwtFromCookie();

  _client = createClient<Database>(url, anonKey, {
    // Per-request REST auth. Deliberately NOT a static
    // global.headers.Authorization: fetchWithAuth only fills the header when
    // absent, so a pinned header freezes REST on the login-time token and
    // starts 401ing at hour 12. Returning null (never '') falls back to the
    // anon key when logged out.
    accessToken: async () => getJwtFromCookie(),
  });

  _builtForSub = sbJwtSubject(jwt);
  _realtimeJwt = jwt;
  // Synchronously — supabase-js also does this from the constructor, but on a
  // microtask, and a component subscribing in the same tick would otherwise
  // join without a token. _performAuth assigns accessTokenValue before its
  // first await when handed an explicit token, so this lands immediately.
  if (jwt) void _client.realtime.setAuth(jwt).catch(() => { _realtimeJwt = null; });

  startCookieWatch();
  return _client;
}

// Lazy proxy — defers createClient until first property access at runtime.
export const supabaseBrowser = new Proxy({} as SupabaseClient<Database>, {
  get(_, prop) {
    const client = getClient();
    const val = (client as any)[prop];
    return typeof val === 'function' ? val.bind(client) : val;
  },
});

/** Force client recreation — only when the LOGGED-IN HUMAN changes.
 *
 *  Guarded on the JWT's `sub`, NOT on the raw token. DataProvider calls this
 *  from an effect keyed on `user?.id`, which fires on mount and again when
 *  /api/auth resolves; unguarded (as shipped in #774) the second call runs
 *  removeAllChannels() on already-subscribed channels, killing realtime at
 *  boot on whichever page won the race. Guarding on the raw token instead
 *  would ALSO break: the client is never rebuilt on rotation, so a token-
 *  keyed marker goes stale after the first ~6h re-mint and every later call
 *  becomes an unconditional nuke with no rebuild path — the RT-2 bug again.
 *  Same subject ⇒ same human ⇒ nothing to reset. */
export function resetSupabaseClient() {
  if (_client && sbJwtSubject(getJwtFromCookie()) === _builtForSub) return;
  if (_client) {
    try { _client.removeAllChannels(); } catch { /* teardown is best-effort */ }
  }
  _client = null;
  _builtForSub = null;
  _realtimeJwt = null;
}
